import { forwardRef, InputHTMLAttributes, useEffect, useState } from 'react';

import { useRecoilState, useSetRecoilState } from 'recoil';

import { createPortal } from 'react-dom';
import {
  feventDpImageDeleteAtom,
  feventDpImageFileNameAtom,
  feventUploadImageAtom,
} from '@service/bugo/containers/FeventDeceasedInfoForm/feventDeceasedInfo.atom';
import { useId } from 'react-id-generator';

import { Button } from './Button';
import { Icon } from './icons';

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  placeholder?: string;
  /** 이미 저장된 파일의 표시용 URL — 새로 고른 파일이 없을 때 미리보기에 사용 */
  savedPreviewUrl?: string;
}

//* 입력칸(.input-box h-11) 높이에 딱 맞는 버튼 — .button-rectangle의 min-h 52px가 칸 밖으로 튀어나오는 것 방지
const baseButtonClass = 'button button-no-border h-full min-h-0 px-[14px] theme-text-4';
//* 미리보기·삭제는 투명 file input 위에 올려 클릭을 직접 받음
const actionButtonClass = `${baseButtonClass} z-10`;
//* 업로드는 클릭을 통과시켜 칸 전체를 덮는 file input이 받게 함 (button이 label 활성화를 막으므로 z-10 금지)
const uploadButtonClass = `${baseButtonClass} pointer-events-none`;

export const FileUploader = forwardRef<HTMLInputElement, Props>(
  ({ placeholder, className, accept, savedPreviewUrl, ...props }, ref) => {
    const { X } = Icon();
    const [id] = useId(1, 'fileuploader');
    const [feventUploadImage, setFeventUploadImage] =
      useRecoilState(feventUploadImageAtom);
    const onFileHandler = async (e: any) => {
      const _file = e.target.files[0];
      if (!_file) return;

      if (!_file.type.startsWith('image/')) {
        alert(
          `파일 "${_file.name}"은(는) 허용되지 않는 파일 형식입니다. 이미지 파일만 업로드하세요.`,
        );
        e.target.value = ''; // input 요소 초기화
        return;
      }
      setFeventUploadImage(_file);
    };

    const [feventDpFileName, setFeventDpFileName] = useRecoilState(
      feventDpImageFileNameAtom,
    );

    const setFeventDpDelete = useSetRecoilState(feventDpImageDeleteAtom);

    //* 새로 고른 파일은 object URL로, 저장된 파일은 버킷 URL로 미리보기
    const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);
    useEffect(() => {
      if (!feventUploadImage) {
        setLocalPreviewUrl(null);
        return;
      }
      const url = URL.createObjectURL(feventUploadImage);
      setLocalPreviewUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    }, [feventUploadImage]);

    const previewUrl = feventUploadImage
      ? localPreviewUrl
      : feventDpFileName
      ? savedPreviewUrl ?? null
      : null;
    const [previewOpen, setPreviewOpen] = useState(false);
    useEffect(() => {
      if (!previewUrl) setPreviewOpen(false);
    }, [previewUrl]);

    const fileName = feventUploadImage ? feventUploadImage.name : feventDpFileName;

    return (
      <>
        <label
          htmlFor={id}
          className={`${className ?? ''} input-box relative flex items-center justify-between pr-0 theme-bg-1 theme-border-1`}
        >
          <input
            {...props}
            ref={ref}
            id={id}
            onChange={(e) => {
              onFileHandler(e);
            }}
            accept={accept}
            className={`absolute left-0 h-full w-full cursor-pointer opacity-0`}
            type="file"
          />
          <div className="min-w-0 flex-1">
            {fileName ? (
              <p className="overflow-x-hidden text-ellipsis whitespace-nowrap">{fileName}</p>
            ) : (
              <p className="theme-text-6">{placeholder ?? '업로드(파일)'}</p>
            )}
          </div>
          <div className="flex h-full shrink-0 items-center py-[1px] pr-[1px]">
            {fileName ? (
              <>
                {previewUrl && (
                  <Button
                    className={`${actionButtonClass} theme-bg-13`}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setPreviewOpen(true);
                    }}
                  >
                    미리보기
                  </Button>
                )}
                <Button
                  className={`${actionButtonClass} ml-[1px] theme-bg-12`}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    if (feventUploadImage) {
                      //* 새로 고른 파일 취소 — 저장된 파일이 있었다면 그대로 유지
                      setFeventUploadImage(null);
                    } else {
                      setFeventDpFileName('');
                      setFeventDpDelete(true);
                    }
                  }}
                >
                  삭제
                </Button>
              </>
            ) : (
              <Button className={`${uploadButtonClass} theme-bg-13`} tabIndex={-1}>
                업로드
              </Button>
            )}
          </div>
        </label>
        {/* 이미지 크게 보기 — label 밖(portal)에 두어 클릭이 파일 선택으로 새지 않게 */}
        {previewOpen &&
          previewUrl &&
          createPortal(
            <div
              className="fixed inset-0 z-[60] flex items-center justify-center overflow-auto bg-black/90 p-4"
              onClick={(e) => {
                e.stopPropagation();
                setPreviewOpen(false);
              }}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setPreviewOpen(false);
                }}
                className="fixed right-4 top-4 rounded-full bg-white/90 p-1.5 text-gray-700 shadow"
                aria-label="닫기"
              >
                <X />
              </button>
              <img
                src={previewUrl}
                alt={fileName ?? '미리보기'}
                className="max-h-full max-w-full cursor-zoom-out object-contain"
              />
            </div>,
            document.body,
          )}
      </>
    );
  },
);
